import React, { useMemo } from 'react';
import { X } from 'lucide-react';
import { InvoiceRow } from '../../../types/common';

interface InvoiceModalProps {
  ym: string | null;
  rows: InvoiceRow[];
  onClose: () => void;
}

/** Deduplicate invoice rows by client, keeping the strongest stage (new > repeat > neither) */
function deduplicateClients(rows: InvoiceRow[]) {
  const map = new Map<string, { name: string; isNew: boolean; isRepeat: boolean }>();
  rows.forEach(r => {
    const key = r.clientId != null ? String(r.clientId) : r.clientName.toLowerCase();
    const existing = map.get(key);
    if (!existing) {
      map.set(key, { name: r.clientName, isNew: r.isNewLogoMonth, isRepeat: r.isRepeatMonth });
    } else {
      map.set(key, {
        name: existing.name,
        isNew: existing.isNew || r.isNewLogoMonth,
        isRepeat: existing.isRepeat || r.isRepeatMonth,
      });
    }
  });
  return Array.from(map.values()).sort((a, b) => {
    // New logo first, then repeat, then neither
    const score = (x: typeof a) => x.isNew ? 2 : x.isRepeat ? 1 : 0;
    return score(b) - score(a) || a.name.localeCompare(b.name);
  });
}

const fmtYM = (ym: string) => {
  const [y, m] = ym.split('-');
  const mon = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  return `${mon[parseInt(m, 10) - 1]} ${y}`;
};

export const InvoiceModal: React.FC<InvoiceModalProps> = ({ ym, rows, onClose }) => {
  const clients = useMemo(() => deduplicateClients(rows), [rows]);

  if (!ym) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl border border-slate-200 w-full max-w-md max-h-[80vh] flex flex-col z-10">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Billing Month: {fmtYM(ym)}</h3>
            <p className="text-xs text-slate-500 mt-0.5">{clients.length} distinct clients</p>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-600 hover:text-slate-700 hover:bg-slate-100 transition-colors"
          >
            <X size={15} />
          </button>
        </div>

        {/* Table */}
        <div className="overflow-y-auto flex-1">
          <table className="w-full border-collapse">
            <thead className="sticky top-0 bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wide text-slate-500">Client</th>
                <th className="px-4 py-2.5 text-center text-[10px] font-semibold uppercase tracking-wide text-slate-500">Stage</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {clients.map((c, i) => (
                <tr key={i} className="hover:bg-slate-50">
                  <td className="px-4 py-2.5 text-sm font-medium text-slate-900">{c.name || '—'}</td>
                  <td className="px-4 py-2.5 text-center">
                    {c.isNew
                      ? <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-700 ring-1 ring-blue-200">New Logo</span>
                      : c.isRepeat
                      ? <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-teal-50 text-teal-700 ring-1 ring-teal-200">Repeat</span>
                      : <span className="text-xs text-slate-600">—</span>
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default InvoiceModal;
